/**
 * Resultado de una acción que devuelve un ENLACE, como panel para copiar.
 *
 * Hay operaciones del ERP cuyo resultado no es un registro cambiado ni un
 * número: es una dirección que alguien tiene que hacerle llegar a otra persona.
 * La primera fue «Invitar al portal» sobre una unidad funcional. Esa acción ya
 * no existe —se fue con el portal del propietario, que se fue con la
 * funcionalidad de expensas— y el panel se queda igual, por lo mismo que lo hizo
 * genérico desde el día uno: se detecta por la FORMA del dato, no por el nombre
 * de la acción, así que no está atado a ningún módulo. El escenario que lo
 * justifica tampoco cambió: **puede no haber servidor de correo**. Ahí un toast
 * que diga «listo» es inútil —el enlace se generó y nadie lo puede leer—, y un
 * `alert()` con la URL cruda es peor: no se puede seleccionar bien desde el
 * celular.
 *
 * Este panel es GENÉRICO, igual que `AgingResultDialog`, y por el mismo motivo:
 * se detecta **por la forma del dato**, no por el nombre de la acción. Cualquier
 * acción de cualquier módulo que devuelva
 *
 *     {"link": {"kind": "access_link", "url": "...", "label": "...",
 *               "share_text": "...", "expires_at": "...", "help": "..."}}
 *
 * obtiene esta pantalla sin tocar una línea de frontend. El texto para compartir
 * lo arma el backend porque nombra los datos del registro, que la pantalla no
 * conoce.
 */

import { useEffect, useRef, useState } from 'react';

import { formatDate } from '@/components/fields/format';
import { IconCheck, IconLink } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import type { ActionResult } from '@/lib/types';

/** Un enlace entregable, tal como lo serializa una acción de servidor. */
export interface EnlaceJson {
  kind: string;
  url: string;
  label?: string;
  help?: string;
  share_text?: string;
  expires_at?: string | null;
}

/** La forma que este panel reconoce. Cambiarla es cambiar el contrato. */
const CLASE_RECONOCIDA = 'access_link';

/**
 * Extrae el enlace de la respuesta de una acción, o `null` si no devolvió uno.
 *
 * Se valida `kind` y que `url` sea una cadena no vacía: sin las dos cosas el
 * panel no se abre y el resultado cae al toast de siempre. Nunca se dibuja un
 * botón «Copiar» que copie `undefined`.
 */
export function leerEnlace(resultado: ActionResult | undefined): EnlaceJson | null {
  if (resultado === undefined) return null;
  const resultados = (resultado as { results?: unknown }).results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;

  const enlace = (datos as { link?: unknown }).link;
  if (enlace === null || typeof enlace !== 'object') return null;

  const { kind, url } = enlace as { kind?: unknown; url?: unknown };
  if (kind !== CLASE_RECONOCIDA) return null;
  if (typeof url !== 'string' || url.trim() === '') return null;
  return enlace as EnlaceJson;
}

/**
 * Copia al portapapeles con respaldo.
 *
 * `navigator.clipboard` no existe fuera de un origen seguro, y `localhost` lo es
 * pero la IP de la red del edificio no. Sin el respaldo, el botón principal de
 * esta pantalla no haría nada justo en el despliegue del cliente.
 */
async function copiar(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard !== undefined && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    // Permiso denegado o contexto inseguro: se prueba el respaldo.
  }
  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export interface LinkResultDialogProps {
  titulo: string;
  datos: EnlaceJson | null;
  onClose: () => void;
}

export function LinkResultDialog({ titulo, datos, onClose }: LinkResultDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const [copiado, setCopiado] = useState<'enlace' | 'mensaje' | null>(null);
  const abierto = datos !== null;

  // Mismo manejo que `AgingResultDialog`, y la dependencia es `abierto` (un
  // booleano) y no `datos`: con el objeto, el efecto se volvería a correr en
  // cada render del padre y le robaría el foco al usuario mientras lee.
  useEffect(() => {
    if (!abierto) return;
    setCopiado(null);
    // Se recuerda el foco de antes para devolverlo al cerrar: quien vino con el
    // teclado tiene que volver al botón que apretó, no al principio de la página.
    focoPrevio.current = document.activeElement as HTMLElement | null;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('#enlace-copiar')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [abierto, onClose]);

  if (datos === null) return null;

  const mensaje = datos.share_text !== undefined && datos.share_text !== '' ? datos.share_text : datos.url;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;

  const alCopiar = (que: 'enlace' | 'mensaje', texto: string) => {
    void copiar(texto).then((ok) => {
      if (ok) {
        setCopiado(que);
        return;
      }
      notify.warning(
        'No se pudo copiar solo',
        'Seleccioná el texto de la caja y copiálo a mano.',
      );
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="enlace-titulo"
      onClick={(evento) => {
        if (evento.target === evento.currentTarget) onClose();
      }}
    >
      <div ref={panel} className="w-full max-w-lg rounded-xl border border-line bg-surface p-5 shadow-lg sm:p-6">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent">
            <IconLink className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 id="enlace-titulo" className="text-lg font-semibold tracking-tight">
              {datos.label !== undefined && datos.label !== '' ? datos.label : titulo}
            </h2>
            {datos.help !== undefined && datos.help !== '' && (
              <p className="mt-1 text-sm text-fg-muted">{datos.help}</p>
            )}
          </div>
        </div>

        {/* El enlace, seleccionable y con quiebre de línea: en un celular la URL
            no entra en una sola y `overflow-hidden` la haría incopiable. */}
        <div className="mt-4 rounded-lg border border-line bg-app-subtle p-3">
          <p className="break-all font-mono text-xs leading-relaxed text-fg">{datos.url}</p>
        </div>

        {datos.expires_at !== undefined && datos.expires_at !== null && datos.expires_at !== '' && (
          <p className="mt-2 text-xs text-fg-faint">
            Vence el {formatDate(datos.expires_at)}. Después hay que generar uno nuevo.
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            id="enlace-copiar"
            variant="primary"
            onClick={() => {
              alCopiar('enlace', datos.url);
            }}
          >
            {copiado === 'enlace' ? (
              <span className="flex items-center gap-1.5">
                <IconCheck className="h-4 w-4" /> Copiado
              </span>
            ) : (
              'Copiar el enlace'
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              alCopiar('mensaje', mensaje);
            }}
          >
            {copiado === 'mensaje' ? (
              <span className="flex items-center gap-1.5">
                <IconCheck className="h-4 w-4" /> Copiado
              </span>
            ) : (
              'Copiar el mensaje'
            )}
          </Button>
          {/* `wa.me` abre WhatsApp Web en escritorio y la aplicación en el
              celular, sin número de destino: lo elige quien manda. */}
          <a
            href={whatsapp}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex h-9 items-center rounded-md border border-line bg-surface px-3 text-sm font-medium transition-colors hover:bg-surface-2"
          >
            Enviar por WhatsApp
          </a>
        </div>

        <div className="mt-5 flex justify-end border-t border-line pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
